import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { VentureCard, VentureCardContent } from "@/components/ui/venture-card";
import { BadgeVenture } from "@/components/ui/badge-venture";
import { TicketThread } from "@/components/support/TicketThread";
import { MessageInput } from "@/components/support/MessageInput";
import { useTicketMessages, type Ticket } from "@/hooks/useTickets";
import { useRole } from "@/contexts/RoleContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

function statusBadgeVariant(status: string) {
  switch (status) {
    case 'open': return 'warning';
    case 'in_progress': return 'info';
    case 'resolved': return 'success';
    case 'closed': return 'default';
    default: return 'default';
  }
}

function statusLabel(status: string) {
  switch (status) {
    case 'in_progress': return 'In Progress';
    default: return status.charAt(0).toUpperCase() + status.slice(1);
  }
}

export default function TicketDetail() {
  const navigate = useNavigate();
  const { ticketId } = useParams<{ ticketId: string }>();
  const { clientId } = useRole();

  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [ticketLoading, setTicketLoading] = useState(true);
  const [senderName, setSenderName] = useState('Client');

  const { messages, loading: messagesLoading, sendMessage } = useTicketMessages(ticketId);

  useEffect(() => {
    if (!ticketId) return;
    async function fetchTicket() {
      setTicketLoading(true);
      try {
        const { data, error } = await supabase
          .from('support_tickets')
          .select('*')
          .eq('id', ticketId)
          .single();
        if (error) throw error;
        setTicket(data as Ticket);
      } catch (err) {
        toast.error('Failed to load ticket');
        navigate('/support');
      } finally {
        setTicketLoading(false);
      }
    }
    fetchTicket();
  }, [ticketId, navigate]);

  useEffect(() => {
    if (!clientId) return;
    async function fetchClientName() {
      const { data } = await supabase
        .from('clients')
        .select('contact_name, company_name')
        .eq('id', clientId)
        .single();
      if (data) {
        setSenderName(data.contact_name || data.company_name || 'Client');
      }
    }
    fetchClientName();
  }, [clientId]);

  const handleSend = async (message: string, file?: File) => {
    await sendMessage({
      message,
      senderType: 'client',
      senderName,
      attachmentFile: file,
    });
  };

  if (ticketLoading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-32 rounded bg-muted/30 animate-pulse" />
        <div className="h-48 rounded-xl bg-muted/30 animate-pulse" />
      </div>
    );
  }

  if (!ticket) return null;

  return (
    <motion.div
      className="space-y-4 flex flex-col h-full"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      {/* Back Button */}
      <button
        onClick={() => navigate('/support')}
        className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors w-fit"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Support
      </button>

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold">{ticket.subject}</h1>
          <p className="text-sm text-muted-foreground mt-0.5 capitalize">{ticket.category.replace('_', ' ')}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <BadgeVenture variant="default" className="text-xs capitalize">
            {ticket.category.replace('_', ' ')}
          </BadgeVenture>
          <BadgeVenture variant={statusBadgeVariant(ticket.status)} className="text-xs">
            {statusLabel(ticket.status)}
          </BadgeVenture>
        </div>
      </div>

      {/* Thread + Message Input */}
      <VentureCard className="flex flex-col flex-1 min-h-[400px]">
        <VentureCardContent className="flex flex-col flex-1 p-0 overflow-hidden">
          {messagesLoading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <TicketThread messages={messages} currentUserType="client" />
          )}
          {ticket.status !== 'closed' && (
            <MessageInput onSend={handleSend} showInternalToggle={false} />
          )}
          {ticket.status === 'closed' && (
            <div className="border-t border-border px-4 py-3 text-center text-sm text-muted-foreground">
              This ticket is closed. Open a new ticket if you need further assistance.
            </div>
          )}
        </VentureCardContent>
      </VentureCard>
    </motion.div>
  );
}
